import { accentColorConfig } from '@hooks/useAccentColor'
import { Agent } from '@typings/dify'
import { AccentColor } from '@typings/index'
import { getChartData } from '@utils/chartData'
import { formatNumber } from '@utils/formatters'
import { MessageSquare, ThumbsUp, X } from 'lucide-react'
import { ReactNode, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts'
import BadgeInfo from './BadgeInfo'

interface DashboardProps {
  agents: Agent[]
  accentColor: AccentColor
  isOpen: boolean
  onClose: () => void
}

const StatCard = ({ icon, label, value, color }: { icon: ReactNode; label: string; value: number; color: string }) => (
  <div className="bg-white dark:bg-gray-800 p-4 rounded-lg">
    <div className="flex items-center space-x-3">
      {icon}
      <div>
        <h3 className="text-sm font-medium text-gray-600 dark:text-gray-400">{label}</h3>
        <p className={`text-2xl font-bold ${color}`}>{formatNumber(value)}</p>
      </div>
    </div>
  </div>
)

const ChartSection = ({ title, data, color }: { title: string; data: unknown[]; color: string }) => (
  <div className="bg-white dark:bg-gray-800 p-4 rounded-lg">
    <h3 className="text-sm font-medium mb-4 dark:text-white">{title}</h3>
    <div className="h-[200px]">
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={data}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="date" />
          <YAxis tickFormatter={formatNumber} />
          <Tooltip formatter={(value: never) => [formatNumber(value), title]} labelFormatter={(label) => `${label}`} />
          <Bar dataKey="value" fill={color} radius={[4, 4, 0, 0]} />
        </BarChart>
      </ResponsiveContainer>
    </div>
  </div>
)

const Dashboard = ({ agents, accentColor, isOpen, onClose }: DashboardProps) => {
  const [activeTab, setActiveTab] = useState<'stats' | 'badges'>('stats')
  const [timeRange, setTimeRange] = useState<'3m' | '6m' | '1y'>('3m')
  const { t } = useTranslation()

  const { messages: messageData, likes: likeData } = useMemo(() => {
    if (!isOpen) return { messages: [], likes: [] }
    return getChartData(agents, timeRange)
  }, [agents, timeRange, isOpen])

  if (!isOpen) return null

  const totalMessages = agents.reduce((sum, agent) => sum + (agent.stats?.messageCount || 0), 0)
  const totalLikes = agents.reduce((sum, agent) => sum + (agent.stats?.likeCount || 0), 0)

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-gray-50 dark:bg-gray-900 rounded-lg w-[90vw] max-w-5xl h-[80vh] flex flex-col">
        {/* Header */}
        <div
          className={`p-4 flex items-center justify-between border-b dark:border-gray-700 bg-white dark:bg-gray-900`}
        >
          <div className="flex items-center space-x-4">
            <h2 className="text-xl font-semibold dark:text-white">{t('dashboard.dashboard')}</h2>
            <div className="flex space-x-2">
              <button
                onClick={() => setActiveTab('stats')}
                className={`px-3 py-1 rounded-lg text-sm ${
                  activeTab === 'stats'
                    ? `${accentColorConfig[accentColor].primary} ${accentColorConfig[accentColor].primary.includes('text-black') ? '' : 'text-white'}`
                    : 'text-gray-600 hover:bg-gray-100 dark:hover:bg-gray-300 dark:text-white dark:hover:text-gray-800'
                }`}
              >
                {t('dashboard.statistics')}
              </button>
              <button
                onClick={() => setActiveTab('badges')}
                className={`px-3 py-1 rounded-lg text-sm ${
                  activeTab === 'badges'
                    ? `${accentColorConfig[accentColor].primary} ${accentColorConfig[accentColor].primary.includes('text-black') ? '' : 'text-white'}`
                    : 'text-gray-600 hover:bg-gray-100 dark:hover:bg-gray-300 dark:text-white dark:hover:text-gray-800'
                }`}
              >
                {t('dashboard.badges')}
              </button>
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-gray-200 dark:text-white dark:hover:bg-gray-700 rounded-lg">
            <X size={20} />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 custom-scrollbar">
          {activeTab === 'stats' ? (
            <div className="space-y-6">
              {/* Stats Cards */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <StatCard
                  icon={<MessageSquare className="text-blue-500" size={24} />}
                  label="Messages"
                  value={totalMessages}
                  color="text-blue-500"
                />
                <StatCard
                  icon={<ThumbsUp className="text-emerald-500" size={24} />}
                  label="Likes"
                  value={totalLikes}
                  color="text-emerald-500"
                />
              </div>

              {/* Time Range Selector */}
              <div className="flex justify-end space-x-2">
                {(['3m', '6m', '1y'] as const).map((range) => (
                  <button
                    key={range}
                    onClick={() => setTimeRange(range)}
                    className={`px-3 py-1 rounded-lg text-sm ${
                      timeRange === range
                        ? `${accentColorConfig[accentColor].primary} ${accentColorConfig[accentColor].primary.includes('text-black') ? '' : 'text-white'}`
                        : 'text-gray-600 hover:bg-gray-100'
                    }`}
                  >
                    {range === '3m' ? t('dashboard.3m') : range === '6m' ? t('dashboard.6m') : t('dashboard.1y')}
                  </button>
                ))}
              </div>

              {/* Charts */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <ChartSection title="Messages" data={messageData} color="#3b82f6" />
                <ChartSection title="Likes" data={likeData} color="#10b981" />
              </div>
            </div>
          ) : (
            <BadgeInfo totalLikes={totalLikes} accentColor={accentColor} />
          )}
        </div>
      </div>
    </div>
  )
}

export default Dashboard
